<template>
  <v-container fluid class="pa-6">
    <v-card>
      <v-card-title class="d-flex align-center justify-space-between flex-wrap gap-2">
        <div class="d-flex align-center">
          <v-icon icon="mdi-video-box" class="mr-2" />
          <span class="d-none d-sm-inline">{{ $t('lessons.title') }}</span>
          <span class="d-inline d-sm-none">Сабақтар</span>
        </div>
        <div class="d-flex gap-2">
          <v-btn 
            color="error" 
            prepend-icon="mdi-delete-sweep" 
            @click="confirmDeleteAllCourses"
            :loading="deletingAll"
            class="d-none d-sm-flex"
          >
            Барлық курстарды жою
          </v-btn>
          <v-tooltip text="Барлық курстарды жою" location="bottom">
            <template #activator="{ props }">
              <v-btn 
                v-bind="props"
                color="error" 
                icon="mdi-delete-sweep" 
                @click="confirmDeleteAllCourses"
                :loading="deletingAll"
                class="d-flex d-sm-none"
                size="small"
              />
            </template>
          </v-tooltip>
          <v-btn 
            color="primary" 
            prepend-icon="mdi-plus" 
            @click="openCategoryDialog()"
            class="d-none d-sm-flex"
          >
            {{ $t('lessons.addCategory') }}
          </v-btn>
          <v-tooltip text="Категория қосу" location="bottom">
            <template #activator="{ props }">
              <v-btn 
                v-bind="props"
                color="primary" 
                icon="mdi-plus" 
                @click="openCategoryDialog()"
                class="d-flex d-sm-none"
                size="small"
              />
            </template>
          </v-tooltip>
        </div>
      </v-card-title>

      <v-card-text>
        <!-- Categories List -->
        <v-expansion-panels v-model="openPanels" multiple>
          <v-expansion-panel
            v-for="category in categories"
            :key="category.id"
            :value="category.id"
          >
            <v-expansion-panel-title>
              <div class="d-flex align-center justify-space-between w-100">
                <div class="d-flex align-center">
                  <v-icon icon="mdi-folder-video" class="mr-3" color="info" />
                  <div>
                    <div class="text-h6">
                      {{ category.titleRu }} / {{ category.titleKz }} / {{ category.titleEn }}
                    </div>
                    <div class="text-caption text-medium-emphasis">
                      {{ category.lessons?.length || 0 }} {{ $t('lessons.lessonsCount') }}
                    </div>
                  </div>
                </div>
                <div class="d-flex align-center" @click.stop>
                  <v-chip :color="category.isActive ? 'success' : 'grey'" size="small" class="mr-2">
                    {{ category.isActive ? $t('common.active') : $t('common.inactive') }}
                  </v-chip>
                  <v-btn
                    icon="mdi-pencil"
                    size="small"
                    variant="text"
                    @click.stop="openCategoryDialog(category)"
                  />
                  <v-btn
                    icon="mdi-delete"
                    size="small"
                    variant="text"
                    color="error"
                    @click.stop="confirmDeleteCategory(category)"
                  />
                </div>
              </div>
            </v-expansion-panel-title>

            <v-expansion-panel-text>
              <div class="mb-4">
                <v-btn
                  color="primary"
                  size="small"
                  prepend-icon="mdi-plus"
                  @click="openLessonDialog(category.id)"
                >
                  {{ $t('lessons.addLesson') }}
                </v-btn>
              </div>

              <!-- Lessons List -->
              <v-list v-if="category.lessons && category.lessons.length > 0">
                <v-list-item
                  v-for="lesson in category.lessons"
                  :key="lesson.id"
                  class="mb-2 border rounded"
                >
                  <template #prepend>
                    <v-avatar color="primary" size="40">
                      <span>{{ lesson.order }}</span>
                    </v-avatar>
                  </template>

                  <v-list-item-title>
                    {{ lesson.titleRu }} / {{ lesson.titleKz }} / {{ lesson.titleEn }}
                  </v-list-item-title>

                  <v-list-item-subtitle>
                    <v-icon size="small" class="mr-1">mdi-clock-outline</v-icon>
                    {{ lesson.duration }} {{ $t('lessons.minutes') }}
                  </v-list-item-subtitle>

                  <template #append>
                    <v-chip :color="lesson.isActive ? 'success' : 'grey'" size="small" class="mr-2">
                      {{ lesson.isActive ? $t('common.active') : $t('common.inactive') }}
                    </v-chip>
                    <v-menu v-if="availableVideos(lesson).length">
                      <template #activator="{ props }">
                        <v-btn
                          icon="mdi-download"
                          size="small"
                          variant="text"
                          color="primary"
                          v-bind="props"
                        />
                      </template>
                      <v-list density="compact">
                        <v-list-item
                          v-for="v in availableVideos(lesson)"
                          :key="v.lang"
                          prepend-icon="mdi-download"
                          :title="`${$t('lessons.downloadVideo') || 'Видео жүктеу'} — ${v.lang}`"
                          @click="downloadVideo(lesson, v)"
                        />
                      </v-list>
                    </v-menu>
                    <v-btn
                      icon="mdi-pencil"
                      size="small"
                      variant="text"
                      @click="openLessonDialog(category.id, lesson)"
                    />
                    <v-btn
                      icon="mdi-delete"
                      size="small"
                      variant="text"
                      color="error"
                      @click="confirmDeleteLesson(lesson)"
                    />
                  </template>
                </v-list-item>
              </v-list>

              <v-alert v-else type="info" variant="tonal" class="mt-2">
                {{ $t('lessons.noCategories') }}
              </v-alert>
            </v-expansion-panel-text>
          </v-expansion-panel>
        </v-expansion-panels>

        <v-alert v-if="categories.length === 0" type="info" class="mt-4">
          {{ $t('lessons.noCategories') }}
        </v-alert>
      </v-card-text>
    </v-card>

    <!-- Category Dialog -->
    <v-dialog v-model="categoryDialog" max-width="800">
      <v-card>
        <v-card-title>
          {{ editingCategory ? $t('lessons.editCategory') : $t('lessons.addCategory') }}
        </v-card-title>
        <v-card-text>
          <v-form ref="categoryFormRef">
            <v-row>
              <v-col cols="12" md="4">
                <v-text-field
                  v-model="categoryForm.titleKz"
                  :label="$t('lessons.categoryTitle') + ' (KZ)'"
                  :rules="[v => !!v || 'Required']"
                  variant="outlined"
                  density="comfortable"
                />
              </v-col>
              <v-col cols="12" md="4">
                <v-text-field
                  v-model="categoryForm.titleRu"
                  :label="$t('lessons.categoryTitle') + ' (RU)'"
                  :rules="[v => !!v || 'Required']"
                  variant="outlined"
                  density="comfortable"
                />
              </v-col>
              <v-col cols="12" md="4">
                <v-text-field
                  v-model="categoryForm.titleEn"
                  :label="$t('lessons.categoryTitle') + ' (EN)'"
                  :rules="[v => !!v || 'Required']"
                  variant="outlined"
                  density="comfortable"
                />
              </v-col>
            </v-row>

            <v-row>
              <v-col cols="12" md="4">
                <v-textarea
                  v-model="categoryForm.descriptionKz"
                  :label="$t('lessons.categoryDescription') + ' (KZ)'"
                  variant="outlined"
                  density="comfortable"
                  rows="3"
                />
              </v-col>
              <v-col cols="12" md="4">
                <v-textarea
                  v-model="categoryForm.descriptionRu"
                  :label="$t('lessons.categoryDescription') + ' (RU)'"
                  variant="outlined"
                  density="comfortable"
                  rows="3"
                />
              </v-col>
              <v-col cols="12" md="4">
                <v-textarea
                  v-model="categoryForm.descriptionEn"
                  :label="$t('lessons.categoryDescription') + ' (EN)'"
                  variant="outlined"
                  density="comfortable"
                  rows="3"
                />
              </v-col>
            </v-row>

            <v-row>
              <v-col cols="12" md="6">
                <v-text-field
                  v-model.number="categoryForm.order"
                  :label="$t('lessons.order')"
                  type="number"
                  variant="outlined"
                  density="comfortable"
                />
              </v-col>
              <v-col cols="12" md="6">
                <v-switch
                  v-model="categoryForm.isActive"
                  :label="$t('lessons.isActive')"
                  color="primary"
                  hide-details
                />
              </v-col>
            </v-row>
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn @click="categoryDialog = false">{{ $t('common.cancel') }}</v-btn>
          <v-btn color="primary" @click="saveCategory" :loading="saving">
            {{ $t('common.save') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Lesson Dialog -->
    <v-dialog v-model="lessonDialog" max-width="900">
      <v-card>
        <v-card-title>
          {{ editingLesson ? $t('lessons.editLesson') : $t('lessons.addLesson') }}
        </v-card-title>
        <v-card-text>
          <v-form ref="lessonFormRef">
            <v-row>
              <v-col cols="12" md="4">
                <v-text-field
                  v-model="lessonForm.titleKz"
                  :label="$t('lessons.lessonTitle') + ' (KZ)'"
                  :rules="[v => !!v || 'Required']"
                  variant="outlined"
                  density="comfortable"
                />
              </v-col>
              <v-col cols="12" md="4">
                <v-text-field
                  v-model="lessonForm.titleRu"
                  :label="$t('lessons.lessonTitle') + ' (RU)'"
                  :rules="[v => !!v || 'Required']"
                  variant="outlined"
                  density="comfortable"
                />
              </v-col>
              <v-col cols="12" md="4">
                <v-text-field
                  v-model="lessonForm.titleEn"
                  :label="$t('lessons.lessonTitle') + ' (EN)'"
                  :rules="[v => !!v || 'Required']"
                  variant="outlined"
                  density="comfortable"
                />
              </v-col>
            </v-row>

            <v-row>
              <v-col cols="12" md="4">
                <v-textarea
                  v-model="lessonForm.descriptionKz"
                  :label="$t('lessons.lessonDescription') + ' (KZ)'"
                  variant="outlined"
                  density="comfortable"
                  rows="3"
                />
              </v-col>
              <v-col cols="12" md="4">
                <v-textarea
                  v-model="lessonForm.descriptionRu"
                  :label="$t('lessons.lessonDescription') + ' (RU)'"
                  variant="outlined"
                  density="comfortable"
                  rows="3"
                />
              </v-col>
              <v-col cols="12" md="4">
                <v-textarea
                  v-model="lessonForm.descriptionEn"
                  :label="$t('lessons.lessonDescription') + ' (EN)'"
                  variant="outlined"
                  density="comfortable"
                  rows="3"
                />
              </v-col>
            </v-row>

            <v-row>
              <v-col cols="12" md="4">
                <v-file-input
                  v-model="videoFileKz"
                  :label="$t('lessons.uploadVideo') + ' (KZ)'"
                  accept="video/*"
                  prepend-icon="mdi-video"
                  variant="outlined"
                  density="comfortable"
                  hint="Қазақ тіліндегі видео (макс. 1GB)"
                  persistent-hint
                  :loading="uploadingVideoKz"
                  @update:model-value="(file) => handleVideoUpload(file, 'kz')"
                >
                  <template #selection="{ fileNames }">
                    <v-chip color="primary" size="small">
                      {{ fileNames[0] }}
                    </v-chip>
                  </template>
                </v-file-input>
                
                <div v-if="lessonForm.videoUrlKz && !uploadingVideoKz" class="mt-2">
                  <v-chip color="success" size="small" prepend-icon="mdi-check-circle">
                    Жүктелді
                  </v-chip>
                  <v-btn icon="mdi-delete" size="x-small" variant="text" color="error" @click="removeVideo('kz')"></v-btn>
                </div>
                
                <v-progress-linear
                  v-if="uploadingVideoKz"
                  indeterminate
                  color="primary"
                  class="mt-2"
                />
              </v-col>

              <v-col cols="12" md="4">
                <v-file-input
                  v-model="videoFileRu"
                  :label="$t('lessons.uploadVideo') + ' (RU)'"
                  accept="video/*"
                  prepend-icon="mdi-video"
                  variant="outlined"
                  density="comfortable"
                  hint="Видео на русском языке (макс. 1GB)"
                  persistent-hint
                  :loading="uploadingVideoRu"
                  @update:model-value="(file) => handleVideoUpload(file, 'ru')"
                >
                  <template #selection="{ fileNames }">
                    <v-chip color="primary" size="small">
                      {{ fileNames[0] }}
                    </v-chip>
                  </template>
                </v-file-input>
                
                <div v-if="lessonForm.videoUrlRu && !uploadingVideoRu" class="mt-2">
                  <v-chip color="success" size="small" prepend-icon="mdi-check-circle">
                    Загружено
                  </v-chip>
                  <v-btn icon="mdi-delete" size="x-small" variant="text" color="error" @click="removeVideo('ru')"></v-btn>
                </div>
                
                <v-progress-linear
                  v-if="uploadingVideoRu"
                  indeterminate
                  color="primary"
                  class="mt-2"
                />
              </v-col>

              <v-col cols="12" md="4">
                <v-file-input
                  v-model="videoFileEn"
                  :label="$t('lessons.uploadVideo') + ' (EN)'"
                  accept="video/*"
                  prepend-icon="mdi-video"
                  variant="outlined"
                  density="comfortable"
                  hint="Video in English (max. 1GB)"
                  persistent-hint
                  :loading="uploadingVideoEn"
                  @update:model-value="(file) => handleVideoUpload(file, 'en')"
                >
                  <template #selection="{ fileNames }">
                    <v-chip color="primary" size="small">
                      {{ fileNames[0] }}
                    </v-chip>
                  </template>
                </v-file-input>
                
                <div v-if="lessonForm.videoUrlEn && !uploadingVideoEn" class="mt-2">
                  <v-chip color="success" size="small" prepend-icon="mdi-check-circle">
                    Uploaded
                  </v-chip>
                  <v-btn icon="mdi-delete" size="x-small" variant="text" color="error" @click="removeVideo('en')"></v-btn>
                </div>
                
                <v-progress-linear
                  v-if="uploadingVideoEn"
                  indeterminate
                  color="primary"
                  class="mt-2"
                />
              </v-col>
            </v-row>

            <v-row>
              <v-col cols="12" md="4">
                <v-text-field
                  v-model.number="lessonForm.duration"
                  :label="$t('lessons.duration')"
                  type="number"
                  variant="outlined"
                  density="comfortable"
                  hint="Минутпен"
                  persistent-hint
                />
              </v-col>
              <v-col cols="12" md="4">
                <v-text-field
                  v-model.number="lessonForm.order"
                  :label="$t('lessons.order')"
                  type="number"
                  variant="outlined"
                  density="comfortable"
                />
              </v-col>
              <v-col cols="12" md="4">
                <v-switch
                  v-model="lessonForm.isActive"
                  :label="$t('lessons.isActive')"
                  color="primary"
                  hide-details
                />
              </v-col>
            </v-row>
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn @click="lessonDialog = false">{{ $t('common.cancel') }}</v-btn>
          <v-btn color="primary" @click="saveLesson" :loading="saving">
            {{ $t('common.save') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Delete Confirmation Dialog -->
    <v-dialog v-model="deleteDialog" max-width="500">
      <v-card>
        <v-card-title>{{ $t('lessons.confirmDelete') }}</v-card-title>
        <v-card-text>
          {{ deleteType === 'category' ? $t('lessons.confirmDeleteCategory') : $t('lessons.confirmDeleteLesson') }}
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn @click="deleteDialog = false">{{ $t('common.cancel') }}</v-btn>
          <v-btn color="error" @click="performDelete" :loading="deleting">
            {{ $t('common.delete') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Delete All Courses Confirmation Dialog -->
    <v-dialog v-model="deleteAllDialog" max-width="600">
      <v-card>
        <v-card-title class="text-h5 text-error">
          <v-icon icon="mdi-alert" class="mr-2" />
          Барлық курстарды жою
        </v-card-title>
        <v-card-text>
          <v-alert type="warning" variant="tonal" class="mb-4">
            <strong>НАЗАР АУДАРЫҢЫЗ:</strong> Бұл әрекет барлық курс категорияларын және 
            сабақтарды толығымен жояды. Бұл әрекетті қайтару мүмкін емес!
          </v-alert>
          <p class="text-body-1">
            Жалпы: <strong>{{ categories.length }}</strong> категория және 
            <strong>{{ totalLessons }}</strong> сабақ жойылады.
          </p>
          <p class="text-body-2 text-medium-emphasis mt-2">
            Растау үшін төмендегі батырманы басыңыз.
          </p>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn @click="deleteAllDialog = false">{{ $t('common.cancel') }}</v-btn>
          <v-btn 
            color="error" 
            @click="deleteAllCourses" 
            :loading="deletingAll"
            prepend-icon="mdi-delete-forever"
          >
            Барлығын жою
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
import api from '@/utils/api'
import { useSnackbarStore } from '@/stores/snackbar'

const { t } = useI18n()
const snackbarStore = useSnackbarStore()

// State
const categories = ref([])
const openPanels = ref([])
const categoryDialog = ref(false)
const lessonDialog = ref(false)
const deleteDialog = ref(false)
const deleteAllDialog = ref(false)
const saving = ref(false)
const deleting = ref(false)
const deletingAll = ref(false)
const editingCategory = ref(null)
const editingLesson = ref(null)
const deleteType = ref('')
const deleteItem = ref(null)

// Computed
const totalLessons = computed(() => {
  return categories.value.reduce((total, cat) => total + (cat.lessons?.length || 0), 0)
})

// Forms
const categoryForm = ref({
  titleKz: '',
  titleRu: '',
  titleEn: '',
  descriptionKz: '',
  descriptionRu: '',
  descriptionEn: '',
  order: 0,
  isActive: true
})

const lessonForm = ref({
  categoryId: null,
  titleKz: '',
  titleRu: '',
  titleEn: '',
  descriptionKz: '',
  descriptionRu: '',
  descriptionEn: '',
  videoUrlKz: '',
  videoUrlRu: '',
  videoUrlEn: '',
  duration: 0,
  order: 0,
  isActive: true
})

const videoFileKz = ref(null)
const videoFileRu = ref(null)
const videoFileEn = ref(null)
const uploadingVideoKz = ref(false)
const uploadingVideoRu = ref(false)
const uploadingVideoEn = ref(false)

// Load categories
const loadCategories = async () => {
  try {
    const response = await api.get('/admin/lesson-categories')
    categories.value = response.data || []
  } catch (error) {
    snackbarStore.error(t('lessons.loadError'))
  }
}

// Category operations
const openCategoryDialog = (category = null) => {
  if (category) {
    editingCategory.value = category
    categoryForm.value = {
      titleKz: category.titleKz,
      titleRu: category.titleRu,
      titleEn: category.titleEn,
      descriptionKz: category.descriptionKz || '',
      descriptionRu: category.descriptionRu || '',
      descriptionEn: category.descriptionEn || '',
      order: category.order,
      isActive: category.isActive
    }
  } else {
    editingCategory.value = null
    categoryForm.value = {
      titleKz: '',
      titleRu: '',
      titleEn: '',
      descriptionKz: '',
      descriptionRu: '',
      descriptionEn: '',
      order: categories.value.length,
      isActive: true
    }
  }
  categoryDialog.value = true
}

const saveCategory = async () => {
  try {
    saving.value = true
    if (editingCategory.value) {
      await api.put(`/admin/lesson-categories/${editingCategory.value.id}`, categoryForm.value)
      snackbarStore.success(t('lessons.categoryUpdated'))
    } else {
      await api.post('/admin/lesson-categories', categoryForm.value)
      snackbarStore.success(t('lessons.categoryCreated'))
    }
    categoryDialog.value = false
    await loadCategories()
  } catch (error) {
    snackbarStore.error(error.response?.data?.error || t('common.error'))
  } finally {
    saving.value = false
  }
}

const confirmDeleteCategory = (category) => {
  deleteType.value = 'category'
  deleteItem.value = category
  deleteDialog.value = true
}

// Video upload handler (with language parameter)
const handleVideoUpload = async (file, language) => {
  if (!file) return
  
  // Validate file size (1000MB = 1GB)
  if (file.size > 1000 * 1024 * 1024) {
    snackbarStore.error('Видео файл өте үлкен (макс. 1GB)')
    if (language === 'kz') videoFileKz.value = null
    else if (language === 'ru') videoFileRu.value = null
    else if (language === 'en') videoFileEn.value = null
    return
  }
  
  try {
    if (language === 'kz') uploadingVideoKz.value = true
    else if (language === 'ru') uploadingVideoRu.value = true
    else if (language === 'en') uploadingVideoEn.value = true
    
    const formData = new FormData()
    formData.append('video', file)
    formData.append('language', language)
    
    const response = await api.post('/admin/lessons/upload-video', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    })
    
    // Set the appropriate videoUrl field based on language
    if (language === 'kz') {
      lessonForm.value.videoUrlKz = response.data.videoUrl
      snackbarStore.success('Қазақша видео жүктелді')
    } else if (language === 'ru') {
      lessonForm.value.videoUrlRu = response.data.videoUrl
      snackbarStore.success('Русское видео загружено')
    } else if (language === 'en') {
      lessonForm.value.videoUrlEn = response.data.videoUrl
      snackbarStore.success('English video uploaded')
    }
  } catch (error) {
    snackbarStore.error('Видео жүктеу кезінде қате: ' + (error.response?.data?.error || error.message))
    if (language === 'kz') videoFileKz.value = null
    else if (language === 'ru') videoFileRu.value = null
    else if (language === 'en') videoFileEn.value = null
  } finally {
    if (language === 'kz') uploadingVideoKz.value = false
    else if (language === 'ru') uploadingVideoRu.value = false
    else if (language === 'en') uploadingVideoEn.value = false
  }
}

// Remove video by language
const removeVideo = (language) => {
  if (language === 'kz') {
    lessonForm.value.videoUrlKz = ''
    videoFileKz.value = null
  } else if (language === 'ru') {
    lessonForm.value.videoUrlRu = ''
    videoFileRu.value = null
  } else if (language === 'en') {
    lessonForm.value.videoUrlEn = ''
    videoFileEn.value = null
  }
}

// Видео жүктеп алу (админ)
const availableVideos = (lesson) => {
  return [
    { lang: 'KZ', url: lesson.videoUrlKz },
    { lang: 'RU', url: lesson.videoUrlRu },
    { lang: 'EN', url: lesson.videoUrlEn }
  ].filter(v => v.url)
}

const downloadVideo = (lesson, video) => {
  try {
    const base = (lesson.titleRu || lesson.titleKz || 'lesson')
      .replace(/[^\wЀ-ӿ -]/g, '').trim().slice(0, 60) || 'lesson'
    const ext = ((video.url.split('/').pop() || '').split('.').pop() || 'mp4').split('?')[0]
    const filename = `${base}_${video.lang}.${ext}`
    const a = document.createElement('a')
    a.href = video.url
    a.download = filename
    document.body.appendChild(a)
    a.click()
    a.remove()
  } catch (e) {
    snackbarStore.error(t('common.error'))
  }
}

// Lesson operations
const openLessonDialog = (categoryId, lesson = null) => {
  // Reset video files
  videoFileKz.value = null
  videoFileRu.value = null
  videoFileEn.value = null
  uploadingVideoKz.value = false
  uploadingVideoRu.value = false
  uploadingVideoEn.value = false
  
  if (lesson) {
    editingLesson.value = lesson
    lessonForm.value = {
      categoryId: lesson.categoryId,
      titleKz: lesson.titleKz,
      titleRu: lesson.titleRu,
      titleEn: lesson.titleEn,
      descriptionKz: lesson.descriptionKz || '',
      descriptionRu: lesson.descriptionRu || '',
      descriptionEn: lesson.descriptionEn || '',
      videoUrlKz: lesson.videoUrlKz || '',
      videoUrlRu: lesson.videoUrlRu || '',
      videoUrlEn: lesson.videoUrlEn || '',
      duration: lesson.duration || 0,
      order: lesson.order,
      isActive: lesson.isActive
    }
  } else {
    editingLesson.value = null
    const category = categories.value.find(c => c.id === categoryId)
    lessonForm.value = {
      categoryId,
      titleKz: '',
      titleRu: '',
      titleEn: '',
      descriptionKz: '',
      descriptionRu: '',
      descriptionEn: '',
      videoUrlKz: '',
      videoUrlRu: '',
      videoUrlEn: '',
      duration: 0,
      order: category?.lessons?.length || 0,
      isActive: true
    }
  }
  lessonDialog.value = true
}

const saveLesson = async () => {
  try {
    saving.value = true
    if (editingLesson.value) {
      await api.put(`/admin/lessons/${editingLesson.value.id}`, lessonForm.value)
      snackbarStore.success(t('lessons.lessonUpdated'))
    } else {
      await api.post('/admin/lessons', lessonForm.value)
      snackbarStore.success(t('lessons.lessonCreated'))
    }
    lessonDialog.value = false
    await loadCategories()
  } catch (error) {
    snackbarStore.error(error.response?.data?.error || t('common.error'))
  } finally {
    saving.value = false
  }
}

const confirmDeleteLesson = (lesson) => {
  deleteType.value = 'lesson'
  deleteItem.value = lesson
  deleteDialog.value = true
}

// Delete operation
const performDelete = async () => {
  try {
    deleting.value = true
    if (deleteType.value === 'category') {
      await api.delete(`/admin/lesson-categories/${deleteItem.value.id}`)
      snackbarStore.success(t('lessons.categoryDeleted'))
    } else {
      await api.delete(`/admin/lessons/${deleteItem.value.id}`)
      snackbarStore.success(t('lessons.lessonDeleted'))
    }
    deleteDialog.value = false
    await loadCategories()
  } catch (error) {
    snackbarStore.error(error.response?.data?.error || t('common.error'))
  } finally {
    deleting.value = false
  }
}

// Delete all courses
const confirmDeleteAllCourses = () => {
  deleteAllDialog.value = true
}

const deleteAllCourses = async () => {
  deletingAll.value = true
  try {
    const response = await api.delete('/admin/courses/all')
    snackbarStore.success(
      `✅ ${response.data.deletedLessons} сабақ және ${response.data.deletedCategories} категория жойылды`
    )
    deleteAllDialog.value = false
    categories.value = []
  } catch (error) {
    snackbarStore.error(error.response?.data?.error || 'Курстарды жою кезінде қате пайда болды')
  } finally {
    deletingAll.value = false
  }
}



onMounted(() => {
  loadCategories()
})
</script>

<style scoped>
.border {
  border: 1px solid rgba(0, 0, 0, 0.12);
}
</style>
